import { SocketService } from '@/services/socket'
import { MessageType } from '@/interfaces/socket'
import { logger } from '@/lib'
import { useGtm } from '@/hooks'
import { GTM_EVENT } from '@/interfaces/gtm'
import { useRouter } from 'next/navigation'
import { useRegistrantAuth } from '@/components/registrant/useRegistrantAuth'
import { StorageFactory } from '@/services'
import { StorageType } from '@/interfaces/storage'
import { AnalyticsSessionService } from '@/services/analyticsSession'

const storage = StorageFactory.getStorage(StorageType.LOCAL)

export const useRegistrantSocket = (type: MessageType) => {
  const { sendDataToGTM } = useGtm()
  const { signInRegistrant } = useRegistrantAuth()
  const router = useRouter()

  const initSocketConnection = (onReconnect: () => void) => {
    SocketService.connect({
      onReconnect,
      onMessage: async (event) => {
        try {
          const message = JSON.parse(event.data)

          if (message.type === type && message.token) {
            SocketService.close()

            // this context received its token through the sign-in waiting
            // page's socket — an auth-link entry (CW2-648 §4.2)
            AnalyticsSessionService.markAuthLinkEntry()

            const { registrant, seasonOrganization } = await signInRegistrant(message.token)
            const userId = `${seasonOrganization?.season?.name}_${seasonOrganization?.organization.midasId}_${registrant?.user.phone}`

            sendDataToGTM({ event: GTM_EVENT.USER_LOGIN })
            sendDataToGTM({ user_id: userId })
            sendDataToGTM({ event: GTM_EVENT.REG_COMPLETED, registrantId: registrant?.id })

            const redirectUrl = message.moreThanOneOrganization
              ? '/registrant/change-organization'
              : '/participant/list'

            router.push(redirectUrl)
          }
        } catch (error) {
          logger.error(error)
          SocketService.close()
        }
      },
    })
  }

  const sendMessage = (payload: Record<string, any>) => {
    SocketService.send({ type, payload })
  }

  return { initSocketConnection, sendMessage }
}
